import {
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
  useThemeProps,
  mergeSlotProps,
} from '@mui/material';
import * as React from 'react';

/**
 * Deprecate props that aren't supported in Unify.
 */
type UnsupportedProps = {
  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  checkedIcon?: MuiCheckboxProps['checkedIcon'];

  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   *
   * @default 'default'
   */
  color?: MuiCheckboxProps['color'];

  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  disableRipple?: MuiCheckboxProps['disableRipple'];

  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  icon?: MuiCheckboxProps['icon'];

  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  readOnly?: MuiCheckboxProps['readOnly'];
};

/**
 * Props that we do not want to include with Checkbox.
 *
 * Using `keyof Pick` provides type safety that ensures we are
 * only blocking props that are part of the interface.
 **/
type CheckboxBlockedProps = keyof Pick<
  MuiCheckboxProps,
  'disableFocusRipple' | 'indeterminateIcon' | 'LinkComponent'
>;

export interface CheckboxProps
  extends Omit<MuiCheckboxProps, keyof UnsupportedProps | CheckboxBlockedProps>,
    UnsupportedProps {
  /**
   * Callback fired when the state is changed.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event The event source of the callback.
   * You can pull out the new checked state by accessing `event.target.checked` (boolean).
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// This wrapper is necessary to change the typing of the `onChange` prop from
// `(event: ChangeEvent, checked:bool) => void` to `(event: ChangeEvent) => void`
// in order to avoid unnecessary typing conflicts with `UiProps.onChange`.
/**
 * Checkboxes allow customers to select multiple items within a list.
 *
 * Demos:
 *
 * - [Checkbox](https://unify.workiva.org/components/checkbox?tab=code)
 *
 * API:
 *
 * - [Checkbox API](https://unify.workiva.org/components/checkbox?tab=props)
 */
export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  function Checkbox(inProps, ref) {
    const props = useThemeProps({ props: inProps, name: 'MuiCheckbox' });

    const {
      onChange,
      indeterminate,
      // eslint-disable-next-line deprecation/deprecation
      inputProps: consumerInputProps,
      slotProps,
      ...rest
    } = props;
    // This is a workaround for MUI not applying `aria-checked="mixed"` attribute: https://github.com/mui/material-ui/issues/20476
    // Only apply if indeterminate is true && consumer hasn't provided their own 'aria-checked'
    const ariaCheckedProps =
      indeterminate && !consumerInputProps?.['aria-checked']
        ? { 'aria-checked': 'mixed' as const }
        : {};
    const mergedInputProps = {
      ...ariaCheckedProps,
      ...consumerInputProps,
    };
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      return onChange?.(event);
    };

    return (
      <MuiCheckbox
        {...rest}
        onChange={handleChange}
        indeterminate={indeterminate}
        slotProps={{
          ...slotProps,
          input: mergeSlotProps(slotProps?.input, mergedInputProps),
        }}
        ref={ref}
      />
    );
  }
);

export default Checkbox;
